import { DayObject } from "@/types/date"
import { TravelerGroup } from "@/types/trip"

import { beforeDayObject, browserToday, nowMillis } from "@/lib/date-utils"
import { PlaceDateKind } from "@/lib/place-utils"
import { totalPeople } from "@/lib/trip-utils"
import { webStorage } from "@/lib/web-storage"

const STORAGE_KEY = "mindtrip.recentPlaceSearchParams" as const

/** Max age for remembered stay and day dates (time since the user picked them). */
export const REMEMBERED_PLACE_DATES_TTL_MS = 30 * 24 * 60 * 60 * 1000

export interface PlaceSearchDateBucket {
  startDate: DayObject
  endDate: DayObject
  updatedAt: number
}

interface RememberedPlaceSearchParams {
  travelers?: TravelerGroup
  stay?: PlaceSearchDateBucket
  day?: PlaceSearchDateBucket
}

export type RememberPlaceSearchParamsInput = {
  travelers?: TravelerGroup
  stay?: PlaceSearchDateBucket
  day?: PlaceSearchDateBucket
}

function isValidDayObject(value: unknown): value is DayObject {
  if (!value || typeof value !== "object") return false

  const { year, month, day } = value as DayObject
  return (
    Number.isInteger(year) &&
    Number.isInteger(month) &&
    month >= 1 &&
    month <= 12 &&
    Number.isInteger(day) &&
    day >= 1 &&
    day <= 31
  )
}

function isValidTravelers(value: unknown): value is TravelerGroup {
  if (!value || typeof value !== "object") return false

  const travelers = value as TravelerGroup
  return (
    typeof travelers.adult_count === "number" &&
    typeof travelers.child_count === "number" &&
    typeof travelers.infant_count === "number" &&
    typeof travelers.pet_count === "number" &&
    totalPeople(travelers) > 0
  )
}

function isValidRememberedDates(
  value: unknown
): value is PlaceSearchDateBucket {
  if (!value || typeof value !== "object") return false

  const dates = value as PlaceSearchDateBucket
  return (
    isValidDayObject(dates.startDate) &&
    isValidDayObject(dates.endDate) &&
    typeof dates.updatedAt === "number" &&
    Number.isFinite(dates.updatedAt)
  )
}

function sameDayObject(a?: DayObject, b?: DayObject): boolean {
  if (!a || !b) return false
  return a.year === b.year && a.month === b.month && a.day === b.day
}

function parseStored(value: unknown): RememberedPlaceSearchParams {
  if (!value || typeof value !== "object") return {}

  const stored = value as RememberedPlaceSearchParams
  const parsed: RememberedPlaceSearchParams = {}

  if (isValidTravelers(stored.travelers)) {
    parsed.travelers = stored.travelers
  }
  if (isValidRememberedDates(stored.stay)) {
    parsed.stay = stored.stay
  }
  if (isValidRememberedDates(stored.day)) {
    parsed.day = stored.day
  }

  return parsed
}

let cachedStored:
  | { raw: string | null; params: RememberedPlaceSearchParams }
  | undefined

/**
 * Keyed on the raw string, which is the cheap half of the read: parsing and
 * validating is not, and on the common empty slot tryGetJSON throws, drops the
 * key and recovers. usePlaceSearchParams asks twice per card render — ~600
 * times on a 96-card grid. Any write, here or in another tab, changes the
 * string and so invalidates this.
 */
function getStored(): RememberedPlaceSearchParams {
  const raw = webStorage.getItem(STORAGE_KEY)
  if (cachedStored?.raw === raw) {
    return cachedStored.params
  }

  const params = parseStored(
    webStorage.tryGetJSON<unknown>(STORAGE_KEY, { defaultValue: {} })
  )
  cachedStored = { raw, params }
  return params
}

export function isPlaceSearchDatesFresh(
  dates: PlaceSearchDateBucket,
  browserTodayParam: DayObject = browserToday()
): boolean {
  if (nowMillis() - dates.updatedAt > REMEMBERED_PLACE_DATES_TTL_MS) {
    return false
  }

  return !beforeDayObject(dates.startDate, browserTodayParam)
}

export function getRememberedPlaceTravelers(): TravelerGroup | undefined {
  return getStored().travelers
}

export function getRememberedPlaceDates(
  kind: PlaceDateKind
): { startDate: DayObject; endDate: DayObject } | undefined {
  const stored = getStored()
  const dates = kind === "stay" ? stored.stay : stored.day
  if (!dates) return undefined

  if (!isPlaceSearchDatesFresh(dates, browserToday())) {
    return undefined
  }

  return { startDate: dates.startDate, endDate: dates.endDate }
}

export function rememberPlaceSearchParams(
  params: RememberPlaceSearchParamsInput | undefined
): void {
  if (!params) return

  const stored = getStored()
  const merged: RememberedPlaceSearchParams = { ...stored }

  if (params.travelers !== undefined && isValidTravelers(params.travelers)) {
    merged.travelers = params.travelers
  }

  if (params.stay !== undefined && isValidRememberedDates(params.stay)) {
    merged.stay = params.stay
  }

  if (params.day !== undefined && isValidRememberedDates(params.day)) {
    merged.day = params.day
  }

  if (
    merged.travelers === undefined &&
    merged.stay === undefined &&
    merged.day === undefined
  ) {
    return
  }

  webStorage.setJSON(STORAGE_KEY, merged)
}

export function rememberPlaceSearchDateBucket(
  startDate: DayObject,
  endDate: DayObject,
  existingBucket?: PlaceSearchDateBucket
): PlaceSearchDateBucket {
  const datesChanged =
    !sameDayObject(existingBucket?.startDate, startDate) ||
    !sameDayObject(existingBucket?.endDate, endDate)

  return {
    startDate,
    endDate,
    updatedAt: datesChanged
      ? nowMillis()
      : (existingBucket?.updatedAt ?? nowMillis()),
  }
}
